// OrderPickupDialog.jsx
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { Dialog } from 'primereact/dialog';
import { Button } from 'primereact/button';
import OrderPickupForm from './OrderPickupForm';
import OrderPickupTablePage from './OrderPickupTable';
import {
  useGetSapInvoiceDetailQuery,
  useInternalOrderPickupMutation,
} from '../../../../api/endpoints/parts';
import { toastService } from '../../../../utils/toastService';
import { useLanguage } from '../../../../contexts/LanguageContext';

const emptyHeader = {
  invoiceNo: '',
  invoiceDate: null,
  totalAmount: '-',
  totalSubAmount: '-',
  totalDiscount: '-',
};

export default function OrderPickupDialog({ visible, onHide, invoiceNo, onSuccess }) {
  const [headerInfo, setHeaderInfo] = useState(emptyHeader);
  const [partData, setPartData] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const { th } = useLanguage();

  const { data, refetch, isFetching } = useGetSapInvoiceDetailQuery(
    { InvoiceNo: invoiceNo ?? '' },
    { skip: !visible || !invoiceNo, refetchOnMountOrArgChange: true }
  );

  const [createPickup, { isLoading: isSaving }] = useInternalOrderPickupMutation();
  const [errors, setErrors] = useState({});

  const mapApiToRows = useCallback((d) => {
    const rows = (d?.parts ?? []).map((p, i) => {
      const totalQty = p.totalQuantity ?? p.quantity ?? 0;
      const receivedQty = p.receivedQuantity ?? 0;
      const unitDiscount = (p.discount ?? 0) / ((p.quantity ?? 1) || 1);
      const totalAmount = p.totalAmount ?? p.amount;

      return {
        id: i + 1,
        partCode: p.partCode,
        partDescreption: p.partName,
        unitPrice: p.unitPrice ?? 0,
        unit: p.mein,
        totalQuantity: totalQty,
        receivedQuantity: receivedQty,
        quantity: 0,
        // warehouseId tablo component'i içinde default ilk id olarak set edilecek
        warehouseId: null,
        totalAmount,
        _unitDiscount: unitDiscount,
        ordrNo: p.ordrn ?? '',
        psnr: p.psnr ?? '',
        dln: p.dln ?? '',
        shelf: p.shelf ?? '',
        eyes: p.eyes ?? '',
      };
    });
    return rows;
  }, []);

  useEffect(() => {
    if (visible && invoiceNo) refetch();
  }, [visible, invoiceNo, refetch]);

  useEffect(() => {
    if (!visible) return;

    const d = data?.data;
    if (d) {
      setHeaderInfo({
        invoiceNo: d.invoiceNo ?? '',
        invoiceDate: d.invoiceDate ? new Date(d.invoiceDate) : null,
        totalAmount: d.totalAmount ?? '-',
        totalSubAmount: d.totalSubAmount ?? '-',
        totalDiscount: d.totalDiscount ?? '-',
      });
      const rows = mapApiToRows(d);
      setPartData(rows);
      setLoaded(true);
    } else {
      setHeaderInfo({ ...emptyHeader, invoiceNo: invoiceNo ?? '' });
      setPartData([]);
      setLoaded(false);
    }

    // Reset errors when dialog opens/closes
    if (!visible) {
      setErrors({});
    }
  }, [visible, data, invoiceNo, mapApiToRows]);

  // Validation function
  const validateForm = () => {
    const newErrors = {};
    
    // Check if at least one part has quantity > 0
    const hasQuantity = partData.some((p) => (p.quantity ?? 0) > 0);
    if (!hasQuantity) {
      newErrors.quantity = th('validation.quantityRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    let rows = partData;

    if (!rows?.length) {
      const res = await refetch();
      const fresh = res?.data?.data;
      if (fresh) {
        rows = mapApiToRows(fresh);
        setPartData(rows);
        setHeaderInfo((prev) => ({
          ...prev,
          invoiceNo: fresh.invoiceNo ?? prev.invoiceNo,
        }));
      }
    }

    // Validate before save
    if (!validateForm()) {
      toastService.error(th('validation.fillRequiredFields'));
      return;
    }

    const warehouses = data?.data?.warehouses ?? [];
    const defaultWarehouseId = warehouses?.[0]?.id ?? 0;

    const payload = {
      invoiceSerialNumber: headerInfo?.invoiceNo || invoiceNo || '',
      description: '',
      parts: (rows || [])
        .filter((p) => p.partCode && (p.quantity ?? 0) > 0)
        .map((p) => ({
          partCode: p.partCode,
          // DOĞRUDAN row.warehouseId kullan (string isim map'i KALDIRILDI)
          warehouseId: p.warehouseId ?? defaultWarehouseId,
          quantity: Number(p.quantity) || 0,
          ordrN: p.ordrNo ?? '',
          psnr: p.psnr ?? '',
        })),
    };

    const result = await createPickup(payload).unwrap();
    if (result && result.isSuccessful) {
      toastService.success(th('warrantyClaimLimit.toastMessages.successSummary'));
      onSuccess?.();
      onHide();
    }
  };

  const footer = useMemo(
    () => (
      <div className="flex gap-2 justify-content-end w-full">
        <Button label={th('common.cancel')} text onClick={onHide} />
        <Button
          label={th('common.save')}
          onClick={handleSave}
          disabled={isFetching || isSaving || !invoiceNo}
        />
      </div>
    ),
    [onHide, isFetching, isSaving, invoiceNo, handleSave]
  );

  return (
    <Dialog
      header={th('parts.partDelivery')}
      visible={visible}
      onHide={onHide}
      style={{ width: 980, maxWidth: 980 }}
      modal
      unmountOnHide
      footer={footer}
    >
      <OrderPickupForm headerInfo={headerInfo} />
      <div style={{ marginTop: 24 }}>
        {errors.quantity && <small className="p-error block mb-2">{errors.quantity}</small>}
        <OrderPickupTablePage
          tableData={partData}
          setTableData={setPartData}
          warehouses={data?.data?.warehouses ?? []}
          invoiceNo={headerInfo.invoiceNo || invoiceNo}
        />
      </div>
    </Dialog>
  );
}
